/* RaynetUI.js — sdílené komponenty pro Raynet 1:1 layout napříč taby
 * klientské složky (Karta, Další údaje, atd.).
 *
 * v4.21.4 (Fáze 3) — SOFT SECTION:
 *  - Hlavička: malá ikona v --accent-soft boxu + UPPERCASE --muted titulek + tenká hairline
 *  - Karta: --surface bg, --line border, 14px radius, --shadow-card
 *  - Yellow varianta (Poznámka ke klientovi) zachována jako warm warning
 */
import { Star, Folder } from "lucide-react";

import { TL } from "@/lib/prekladyUI";
/* RaynetSection — block with SOFT muted header.
 * Replaces the old solid colored header bar with a calm bento card. */
export function RaynetSection({ title, children, extra, yellow = false, icon: Icon = Folder }) {
  if (yellow) {
    return (
      <section
        className="overflow-hidden"
        style={{
          background: "#FFFDF5",
          border: "1px solid rgba(202, 154, 60, 0.30)",
          borderRadius: 14,
          boxShadow: "var(--shadow-card)",
        }}
      >
        <header
          className="flex items-center gap-2 px-3 pt-2.5 pb-1.5"
          style={{ borderBottom: "1px solid rgba(202, 154, 60, 0.22)" }}
        >
          <span
            className="inline-flex items-center justify-center rounded-md"
            style={{ width: 18, height: 18, background: "rgba(202, 154, 60, 0.18)", color: "#9C7B2E" }}
          >
            <Icon size={11} strokeWidth={2.4} />
          </span>
          <div
            className="text-popisek font-bold uppercase tracking-[0.08em] flex-1 truncate"
            style={{ color: "#9C7B2E" }}
          >
            {title}
          </div>
          {extra}
        </header>
        <div className="px-3 py-2">{children}</div>
      </section>
    );
  }
  return (
    <section
      className="overflow-hidden"
      style={{
        background: "var(--surface)",
        border: "1px solid var(--line)",
        borderRadius: 14,
        boxShadow: "var(--shadow-card)",
      }}
    >
      <header
        className="flex items-center gap-2 px-3 pt-2.5 pb-1.5"
        style={{ borderBottom: "1px solid var(--line)" }}
      >
        <span
          className="inline-flex items-center justify-center rounded-md"
          style={{ width: 18, height: 18, background: "var(--accent-soft)", color: "var(--accent)" }}
        >
          <Icon size={11} strokeWidth={2.4} />
        </span>
        <div
          className="text-popisek font-bold uppercase tracking-[0.08em] flex-1 truncate"
          style={{ color: "var(--muted)" }}
        >
          {title}
        </div>
        {extra}
      </header>
      <div className="px-3 py-2">{children}</div>
    </section>
  );
}

/* RaynetField — single row: label left (fixed width), input right (flex-1). */
export function RaynetField({ label, children, labelWidth = 78 }) {
  return (
    <div className="flex items-center gap-1.5 py-0.5">
      <div
        className="text-drobny font-medium flex-shrink-0"
        style={{ color: "var(--muted)", width: labelWidth }}
      >
        {label}:
      </div>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

/* InputTight — compact text input matching Raynet density (28px height, 12.5px font)
 *
 * readOnly = pole jen ZOBRAZUJE hodnotu ze systému a nejde do něj psát.
 * PROČ tu ten režim je: dřív se pro needitovatelné údaje používal normální input
 * s `onChange={() => {}}`. Vypadal úplně stejně jako editovatelné pole (stejné
 * pozadí, stejný accent rámeček při kliknutí), ale poradce do něj nenapsal ani
 * znak — řízený input hodnotu okamžitě vrátil zpět. Buď se pole ukládá, nebo je
 * na první pohled jen ke čtení; nic mezi tím. Proto readOnly navíc tlumí pozadí
 * i text a nerozsvěcuje accent rámeček při focusu. */
export function InputTight({ value, onChange, type = "text", testid, placeholder, readOnly = false, title }) {
  return (
    <input
      type={type}
      value={value ?? ""}
      readOnly={readOnly}
      title={title}
      onChange={(e) => { if (!readOnly) onChange?.(e.target.value); }}
      data-testid={testid}
      placeholder={placeholder}
      className="w-full rounded px-2 py-1 text-maly outline-none transition-colors"
      style={{
        background: readOnly ? "var(--surface-2)" : "var(--surface)",
        border: "1px solid var(--line)",
        color: readOnly ? "var(--muted)" : "var(--ink)",
        height: 28,
        cursor: readOnly ? "default" : "text",
      }}
      onFocus={(e) => { if (!readOnly) e.target.style.borderColor = "var(--accent)"; }}
      onBlur={(e) => (e.target.style.borderColor = "var(--line)")}
    />
  );
}

/* SelectTight — compact select matching InputTight density */
export function SelectTight({ value, onChange, options, testid, minWidth }) {
  return (
    <select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="rounded px-1.5 py-1 text-[12px] outline-none cursor-pointer"
      style={{
        background: "var(--surface)",
        border: "1px solid var(--line)",
        color: "var(--ink)",
        height: 28,
        // paddingTop/Bottom 0 SCHVÁLNĚ: .apple-skin select má v apple-skin.css
        // padding 10px 14px, což u pevné výšky 28 px vytlačilo vybraný text pod
        // viditelnou oblast → select vypadal PRÁZDNÝ, i když hodnota byla vybraná
        // (nahlášeno 21. 9. 2026 u pole „Zdroj kontaktu"). Inline styl to přebije.
        paddingTop: 0,
        paddingBottom: 0,
        width: minWidth || "100%",
        minWidth: minWidth || 0,
      }}
    >
      {options.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
    </select>
  );
}

/* RatingStars — 3-star rating with accent fill */
export function RatingStars({ value = 0, onChange }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[12px] font-bold" style={{ color: "var(--ink)" }}>
        {value >= 4 ? "A" : value >= 3 ? "B" : value >= 2 ? "C" : "—"}
      </span>
      <div className="flex items-center gap-0.5">
        {[1, 2, 3].map((i) => (
          <button aria-label={`${TL("Hodnocení")} ${i}/3`}
            key={i}
            onClick={() => onChange(i + 2)}
            className="transition-transform hover:scale-110"
            type="button"
          >
            <Star
              size={12}
              fill={value >= i + 2 ? "var(--accent)" : "transparent"}
              stroke={value >= i + 2 ? "var(--accent)" : "var(--faint)"}
              strokeWidth={1.5}
            />
          </button>
        ))}
      </div>
    </div>
  );
}
